import React from "react";
import turkeyIcon from "../assets/thanksgiving-turkey.png";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// renderFlagsWithIcons — swap emoji placeholders in a tile title for real
// image icons (#514).
//
// WHY THIS EXISTS. The tile's flag row is a single concatenated STRING built by
// computeFlagsEmojiString and rendered as text (JobTile reads
// `encodedSmallTitle` / `encodedLargeTitle`). An <img> cannot live inside a
// string, so shipping an artwork icon means turning that string into an array
// of React nodes at render time — which is all this does. Everything upstream
// (the projection, the emoji string, the tooltip matching, every test that
// asserts on `.includes("🦃")`) is untouched and still works on the string.
//
// Only the Thanksgiving flag has artwork today (Leland, 2026-09-10, from
// SUP-9966 via #514). The rest stay as emoji deliberately — replacing them was
// not asked for, and each one would need its own asset.
//
// ⚠️ The file is a PNG despite the issue calling it an SVG. It is 800×800, so
// it is sized in `em` to track the surrounding text rather than a fixed pixel
// box; that keeps it right in both the small and large tile views without a
// second asset.
//
// Pure and importable so tests exercise the real code path, per CLAUDE.md's
// Code Review Philosophy.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** The emoji each artwork icon replaces. Keyed by the character that
 *  computeFlagsEmojiString emits, so the two cannot drift apart silently. */
export const FLAG_ICONS = {
  "🦃": { src: turkeyIcon, alt: "Thanksgiving rush" },
};

/**
 * Split `text` on any emoji that has artwork and return an array of React
 * nodes, with those emoji replaced by <img>. Returns the input unchanged (as a
 * single-element array) when it contains none, so the common path allocates
 * nothing extra.
 *
 * @param {string | undefined | null} text
 * @returns {Array<string | React.ReactElement>}
 */
export function renderFlagsWithIcons(text) {
  if (typeof text !== "string" || text === "") return [text ?? ""];

  const chars = Object.keys(FLAG_ICONS);
  if (!chars.some((c) => text.includes(c))) return [text];

  // Split on the icon characters, KEEPING them (capturing group), so the
  // surrounding text survives in order.
  const pattern = new RegExp(`(${chars.map(escapeRegExp).join("|")})`, "g");
  return text.split(pattern).map((part, i) => {
    const icon = FLAG_ICONS[part];
    if (!icon) return part;
    return (
      <img
        key={`flag-icon-${i}`}
        src={icon.src}
        alt={icon.alt}
        title={icon.alt}
        // Sized in em so it tracks the tile's font size in both views.
        className="inline-block h-[1.15em] w-[1.15em] align-[-0.2em] object-contain"
      />
    );
  });
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
